import React, { useState } from "react";
import {
  HiCheckCircle,
  HiChevronRight,
  HiTrash,
  HiXCircle,
} from "react-icons/hi";
import { AgentEvaluationRun } from "../../../services/api";
import {
  formatPercent,
  formatRelativeTime,
  getStatusClasses,
  shortId,
  statusLabel,
} from "./shared";
import { UseAgentTestLab } from "./useAgentTestLab";

interface RunsTabProps {
  lab: UseAgentTestLab;
}

export const RunsTab: React.FC<RunsTabProps> = ({ lab }) => {
  if (lab.runs.length === 0) {
    return (
      <section className="bg-[var(--card)] border border-[var(--border)] rounded-lg p-8 text-center text-sm text-[var(--foreground-muted)]">
        Run scenarios to start building a history. Each run is timestamped and
        scored against the current prompt.
      </section>
    );
  }

  return (
    <section className="bg-[var(--card)] border border-[var(--border)] rounded-lg overflow-hidden">
      <ul className="divide-y divide-[var(--border)]">
        {lab.runs.map((run, index) => {
          const previous = lab.runs[index + 1];
          return <RunRow key={run.id} run={run} previous={previous} lab={lab} />;
        })}
      </ul>
    </section>
  );
};

const RunRow: React.FC<{
  run: AgentEvaluationRun;
  previous?: AgentEvaluationRun;
  lab: UseAgentTestLab;
}> = ({ run, previous, lab }) => {
  const [expanded, setExpanded] = useState(false);
  const passed = run.results.filter((r) => r.verdict === "pass").length;
  const failed = run.results.length - passed;
  const delta =
    typeof previous?.accuracy_score === "number"
      ? (run.accuracy_score ?? 0) - previous.accuracy_score
      : null;

  return (
    <li>
      <div className="w-full px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 hover:bg-[var(--muted)] transition-colors">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="min-w-0 flex flex-1 items-center gap-4 text-left"
          aria-expanded={expanded}
        >
          <HiChevronRight
            className={`w-4 h-4 text-[var(--foreground-muted)] transition-transform flex-shrink-0 ${
              expanded ? "rotate-90" : ""
            }`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium tabular-nums">
                {formatPercent(run.accuracy_score)}
              </span>
              {delta !== null && (
                <span
                  className={`text-xs tabular-nums ${
                    delta > 0
                      ? "text-green-600"
                      : delta < 0
                        ? "text-red-600"
                        : "text-[var(--foreground-muted)]"
                  }`}
                >
                  {delta > 0 ? "+" : ""}
                  {formatPercent(delta)}
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded-full border text-[10px] capitalize ${getStatusClasses(
                  run.status
                )}`}
              >
                {statusLabel(run.status)}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-[var(--foreground-muted)]">
                {run.run_type}
              </span>
            </div>
            <div className="text-xs text-[var(--foreground-muted)] mt-0.5">
              {formatRelativeTime(run.started_at ?? run.created_at)} ·{" "}
              {run.results.length} scenarios · run {shortId(run.id)}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-3 text-xs tabular-nums">
          <span className="inline-flex items-center gap-1 text-green-600">
            <HiCheckCircle className="w-3.5 h-3.5" />
            {passed}
          </span>
          <span className="inline-flex items-center gap-1 text-red-600">
            <HiXCircle className="w-3.5 h-3.5" />
            {failed}
          </span>
        </div>
        <button
          type="button"
          onClick={() => lab.requestDeleteRun(run)}
          disabled={lab.deletingRunId === run.id}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--foreground-muted)] hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={`Delete run ${shortId(run.id)}`}
          title="Delete run from history"
        >
          <HiTrash className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 bg-[var(--card-container)] border-t border-[var(--border)]">
          <ul className="space-y-1">
            {run.results.map((result) => {
              const scenario = lab.scenarios.find(
                (s) => s.id === result.scenario_id
              );
              const isFail =
                result.verdict !== "pass" || result.critical_failure;
              return (
                <li key={result.id}>
                  <button
                    type="button"
                    onClick={() => {
                      lab.setSelectedScenarioId(result.scenario_id);
                      lab.setActiveTab("scenarios");
                      lab.setEditorSubTab("verdict");
                    }}
                    className="w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-[var(--muted)] text-sm"
                  >
                    {isFail ? (
                      <HiXCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    ) : (
                      <HiCheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    )}
                    <span className="flex-1 truncate">
                      {scenario?.title ?? result.scenario_id}
                    </span>
                    <span className="text-xs text-[var(--foreground-muted)] tabular-nums">
                      {formatPercent(result.score)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </li>
  );
};
